"use client"

import { FC, useRef } from "react"
import { Content } from "@prismicio/client"
import { PrismicRichText, SliceComponentProps } from "@prismicio/react"
import {
  LazyMotion,
  domAnimation,
  m,
  motion,
  useScroll,
  useTransform,
  useInView,
  type Variants,
} from "motion/react"
import { cn, sliceLabel } from "@/lib/utils"
import { vFadeUp, vStaggerContainer } from "@/lib/animations"

export type HeadingDescriptionHighlightedQuoteProps =
  SliceComponentProps<Content.HeadingDescriptionHighlightedQuoteSlice>

const vQuoteReveal: Variants = {
  hidden: { opacity: 0, x: -36, skewX: -1 },
  visible: {
    opacity: 1,
    x: 0,
    skewX: 0,
    transition: { duration: 0.75, ease: [0.34, 1.56, 0.64, 1] },
  },
}

const vCitationCard: Variants = {
  hidden: { opacity: 0, y: 28, scale: 0.96 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.55, ease: "easeOut", delay: i * 0.13 },
  }),
}

// Dot-mandala SVG rendered as decoration
function DotMandala() {
  return (
    <svg viewBox="0 0 100 100" className="size-full">
      <circle cx="50" cy="50" r="2.5" fill="var(--sacred-orange)" />
      {Array.from({ length: 8 }, (_, i) => {
        const rad = ((i * 360) / 8) * (Math.PI / 180)
        return (
          <circle
            key={`r1-${i}`}
            cx={50 + 15 * Math.sin(rad)}
            cy={50 - 15 * Math.cos(rad)}
            r="2"
            fill="var(--sacred-orange)"
          />
        )
      })}
      {Array.from({ length: 12 }, (_, i) => {
        const rad = ((i * 360) / 12) * (Math.PI / 180)
        return (
          <circle
            key={`r2-${i}`}
            cx={50 + 30 * Math.sin(rad)}
            cy={50 - 30 * Math.cos(rad)}
            r="1.5"
            fill="var(--sacred-gold)"
          />
        )
      })}
      {Array.from({ length: 16 }, (_, i) => {
        const rad = ((i * 360) / 16) * (Math.PI / 180)
        return (
          <circle
            key={`r3-${i}`}
            cx={50 + 44 * Math.sin(rad)}
            cy={50 - 44 * Math.cos(rad)}
            r="1"
            fill="var(--sacred-red)"
          />
        )
      })}
    </svg>
  )
}

const FLOATERS = [
  { symbol: "ॐ", top: "10%", left: "3%", size: "text-4xl", dur: 5, delay: 0 },
  { symbol: "✦", top: "72%", left: "6%", size: "text-2xl", dur: 4, delay: 0.6 },
  {
    symbol: "ॐ",
    top: "18%",
    right: "4%",
    size: "text-3xl",
    dur: 4.5,
    delay: 1.1,
  },
  {
    symbol: "✦",
    top: "58%",
    right: "5%",
    size: "text-xl",
    dur: 3.8,
    delay: 0.3,
  },
  { symbol: "꩜", top: "85%", left: "18%", size: "text-lg", dur: 6, delay: 0.8 },
  {
    symbol: "꩜",
    top: "5%",
    right: "20%",
    size: "text-base",
    dur: 5.5,
    delay: 1.5,
  },
] as const

const HeadingDescriptionHighlightedQuote: FC<
  HeadingDescriptionHighlightedQuoteProps
> = ({ slice }) => {
  const { primary } = slice
  const sectionRef = useRef<HTMLElement>(null)
  const isInView = useInView(sectionRef, { once: true, margin: "-80px" })

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"],
  })
  const mandalaY = useTransform(scrollYProgress, [0, 1], ["0%", "-18%"])

  const alignClass = primary.text_align ? "text-left" : "text-center"
  const itemsClass = primary.text_align ? "items-start" : "items-center"

  return (
    <LazyMotion features={domAnimation}>
      <section
        ref={sectionRef}
        data-slice-type={slice.slice_type}
        data-slice-variation={slice.variation}
        aria-label={sliceLabel(slice.slice_type)}
        className="relative overflow-hidden bg-sacred-cream section-py"
      >
        {/* ── Background decorations ── */}

        {/* Giant watermark Om — slow parallax rotation */}
        <m.div
          aria-hidden
          className="pointer-events-none absolute top-2 right-2 text-[20rem] leading-none font-bold select-none"
          style={{
            background:
              "linear-gradient(135deg, var(--sacred-orange), var(--sacred-gold))",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }}
          animate={{
            scale: [1, 1.06, 1],
            opacity: [0.12, 0.22, 0.12],
          }}
          transition={{
            duration: 6,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        >
          ॐ
        </m.div>

        {/* Dot mandala bottom-left */}
        <m.div
          aria-hidden
          className="pointer-events-none absolute -bottom-20 -left-20 size-80 opacity-[0.08]"
          style={{ y: mandalaY }}
        >
          <DotMandala />
        </m.div>

        {/* Chakra line top */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 top-0 h-px"
          style={{
            background:
              "linear-gradient(to right, transparent, var(--sacred-gold) 30%, var(--sacred-orange) 50%, var(--sacred-gold) 70%, transparent)",
          }}
        />
        {/* Chakra line bottom */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 bottom-0 h-px"
          style={{
            background:
              "linear-gradient(to right, transparent, var(--sacred-gold) 30%, var(--sacred-orange) 50%, var(--sacred-gold) 70%, transparent)",
          }}
        />

        {/* Floating Vedic symbols */}
        {FLOATERS.map((el, i) => (
          <m.div
            key={i}
            aria-hidden
            className={cn(
              "pointer-events-none absolute font-bold text-sacred-orange/15 select-none",
              el.size,
            )}
            style={{
              top: el.top,
              left: "left" in el ? el.left : undefined,
              right: "right" in el ? el.right : undefined,
            }}
            animate={{ y: [-7, 7, -7], opacity: [0.08, 0.2, 0.08] }}
            transition={{
              duration: el.dur,
              repeat: Infinity,
              ease: "easeInOut",
              delay: el.delay,
            }}
          >
            {el.symbol}
          </m.div>
        ))}

        {/* ── Main content ── */}
        <m.div
          className={cn(
            "relative z-10 container flex max-w-3xl flex-col gap-10",
            itemsClass,
            alignClass,
          )}
          variants={vStaggerContainer}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
        >
          {/* Heading with ornamental eyebrow + gradient underline */}
          {primary.heading && (
            <m.div variants={vFadeUp} className={cn("max-w-3xl", alignClass)}>
              {/* Eyebrow */}
              <div
                className={cn(
                  "mb-3 flex items-center gap-2",
                  primary.text_align ? "justify-start" : "justify-center",
                )}
              >
                <span className="text-xs text-sacred-gold">✦</span>
                <span className="text-xs font-semibold tracking-[0.28em] text-sacred-orange/60 uppercase">
                  Vedic Wisdom
                </span>
                <span className="text-xs text-sacred-gold">✦</span>
              </div>

              <PrismicRichText field={primary.heading} />

              {/* Animated gradient underline */}
              <m.div
                className={cn(
                  "mt-3 h-0.5 w-16 rounded-full",
                  primary.text_align ? "" : "mx-auto",
                )}
                style={{
                  background:
                    "linear-gradient(to right, var(--sacred-orange), var(--sacred-gold))",
                  originX: primary.text_align ? 0 : 0.5,
                }}
                variants={{
                  hidden: { scaleX: 0, opacity: 0 },
                  visible: {
                    scaleX: 1,
                    opacity: 1,
                    transition: { duration: 0.9, ease: "easeOut", delay: 0.25 },
                  },
                }}
              />
            </m.div>
          )}

          {/* Description */}
          {primary.description && (
            <m.div variants={vFadeUp} className={cn("max-w-2xl", alignClass)}>
              <PrismicRichText field={primary.description} />
            </m.div>
          )}

          {/* Highlighted Quote — glowing gradient-border card */}
          {primary.highlighted_quote &&
            primary.highlighted_quote.length > 0 && (
              <m.div
                variants={vQuoteReveal}
                className={cn(
                  "relative w-full max-w-3xl",
                  primary.text_align ? "" : "mx-auto",
                )}
              >
                {/* Corner lotus ornaments */}
                <span
                  aria-hidden
                  className="pointer-events-none absolute -top-3 -left-2 text-2xl text-sacred-gold/50 select-none"
                >
                  ❋
                </span>
                <span
                  aria-hidden
                  className="pointer-events-none absolute -right-2 -bottom-3 rotate-180 text-2xl text-sacred-gold/50 select-none"
                >
                  ❋
                </span>

                {/* Gradient border via p-px wrapper */}
                <div
                  className="rounded-2xl p-px"
                  style={{
                    background:
                      "linear-gradient(135deg, var(--sacred-orange), var(--sacred-gold) 50%, var(--sacred-orange))",
                  }}
                >
                  {/* Inner card */}
                  <div
                    className="rounded-2xl px-7 py-6"
                    style={{
                      background:
                        "linear-gradient(160deg, oklch(0.985 0.018 75), oklch(0.96 0.03 72))",
                    }}
                  >
                    <div className="flex gap-5">
                      {/* Left gradient bar */}
                      <div
                        className="w-0.75 shrink-0 rounded-full"
                        style={{
                          background:
                            "linear-gradient(to bottom, var(--sacred-orange), var(--sacred-gold))",
                        }}
                      />
                      <div>
                        {(() => {
                          let pIdx = 0
                          return (
                            <PrismicRichText
                              field={primary.highlighted_quote}
                              components={{
                                paragraph: ({ children }) => {
                                  const idx = pIdx++
                                  if (idx === 0)
                                    return (
                                      <p className="text-lg/relaxed font-semibold text-sacred-orange">
                                        {children}
                                      </p>
                                    )
                                  return (
                                    <p
                                      className={cn(
                                        "mt-2 text-base/relaxed text-gray-700",
                                        idx === 1 && "italic",
                                      )}
                                    >
                                      {children}
                                    </p>
                                  )
                                },
                              }}
                            />
                          )
                        })()}
                      </div>
                    </div>
                  </div>
                </div>
              </m.div>
            )}

          {/* Sanskrit citation cards */}
          {primary.additional_highlighted &&
            primary.additional_highlighted.length > 0 && (
              <div className="flex w-full max-w-3xl flex-col gap-4">
                {primary.additional_highlighted.map((item, i) => (
                  <m.div
                    key={i}
                    custom={i}
                    variants={vCitationCard}
                    className="group relative overflow-hidden rounded-2xl"
                    style={{
                      background:
                        "linear-gradient(145deg, oklch(1 0.012 75 / 0.92), oklch(0.97 0.022 74))",
                      boxShadow:
                        "0 2px 20px oklch(0.65 0.14 42 / 0.08), inset 0 1px 0 oklch(1 0 0 / 0.7)",
                    }}
                  >
                    {/* Hover shimmer sweep */}
                    <motion.div
                      className="pointer-events-none absolute inset-0 -translate-x-full"
                      style={{
                        background:
                          "linear-gradient(105deg, transparent 35%, oklch(0.97 0.04 75 / 0.55) 50%, transparent 65%)",
                      }}
                      whileHover={{ translateX: "100%" }}
                      transition={{ duration: 0.55, ease: "easeInOut" }}
                    />

                    {/* Thin top accent */}
                    <div
                      className="absolute inset-x-0 top-0 h-px"
                      style={{
                        background:
                          "linear-gradient(to right, transparent, var(--sacred-gold) 40%, var(--sacred-orange) 60%, transparent)",
                      }}
                    />

                    <div className="relative flex items-start gap-4 px-6 py-5">
                      {/* Om glyph marker */}
                      <div
                        aria-hidden
                        className="mt-0.5 shrink-0 text-xl leading-none text-sacred-orange/50"
                      >
                        ॐ
                      </div>
                      <div>
                        {item.original_text && (
                          <p className="text-base font-semibold text-sacred-red">
                            {item.original_text}
                          </p>
                        )}
                        {item.translation && (
                          <p className="mt-1 text-sm text-gray-500 italic">
                            {item.translation}
                          </p>
                        )}
                      </div>
                    </div>
                  </m.div>
                ))}
              </div>
            )}
        </m.div>
      </section>
    </LazyMotion>
  )
}

export default HeadingDescriptionHighlightedQuote
